import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { products } from '../data/products';

const ElectricSkateboardsPage = () => {
  const { i18n } = useTranslation();
  
  const skateboardProducts = products.filter(product =>
    product.brand === 'blueboard' && product.category === 'electric-skateboard'
  );

  // Separate BlueBoard products to show at top
  const blueboardProducts = products.filter(product => 
    product.brand === 'blueboard' && product.category === 'electric-skateboard'
  );
  const otherSkateboardProducts = skateboardProducts.filter(product =>
    !(product.brand === 'blueboard' && product.category === 'electric-skateboard')
  );



  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Electric Skateboards - BlueBoard Israel",
    "description": "Premium electric skateboards from BlueBoard in Israel.",
    "url": "https://blueboard.bloodshop.org/electric-skateboards",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": skateboardProducts.map((product, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": product.translations[i18n.language as 'en' | 'he'].name,
          "description": product.translations[i18n.language as 'en' | 'he'].description,
          "image": product.image,
          "url": `https://blueboard.bloodshop.org/products/${product.id}`,
          "brand": {
            "@type": "Brand",
            "name": "BlueBoard"
          }
        }
      }))
    }
  };

  return (
    <>
      <SEO
        title="Electric Skateboards - BlueBoard | Israel"
        description="Discover BlueBoard electric skateboards designed and built for riders in Israel."
        keywords="BlueBoard electric skateboard, electric skateboard Israel, made in Israel, Tel Aviv, Jerusalem, Haifa, electric vehicle, e-skate"
        url="/electric-skateboards"
        type="website"
        lang={i18n.language as 'en' | 'he'}
        structuredData={structuredData}
      />
      
      <div className="bg-white min-h-screen py-12" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">
              {i18n.language === 'he' ? 'סקייטבורדים חשמליים' : 'Electric Skateboards'}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {i18n.language === 'he' 
                ? 'גלה את הסקייטבורדים החשמליים של BlueBoard, שפותחו ונבנו עבור רוכבים בישראל.'
                : 'Discover BlueBoard electric skateboards developed and built for riders in Israel.'
              }
            </p>
          </div>

          {/* BlueBoard Featured Section */}
          {blueboardProducts.length > 0 && (
            <div className="mb-16">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
                  {i18n.language === 'he' ? 'BlueBoard - הסקייטבורד שלנו' : 'BlueBoard - Our Signature Skateboard'}
                </h2>
                <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                  {i18n.language === 'he' 
                    ? 'הסקייטבורד החשמלי המקורי שלנו - מעוצב ומיוצר בישראל עבור הרוכבים הישראלים'
                    : 'Our original electric skateboard - designed and manufactured in Israel for Israeli riders'
                  }
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {blueboardProducts.map((product) => (
                  <div key={product.id} className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl shadow-xl">
                    <div className="text-center lg:text-left">
                      <h3 className="text-2xl font-bold text-blue-900 mb-4">
                        {product.translations[i18n.language as 'en' | 'he'].name}
                      </h3>
                      <p className="text-lg text-blue-700 mb-6">
                        {product.translations[i18n.language as 'en' | 'he'].description}
                      </p>
                      {typeof product.price === 'number' && (
                        <div className="text-3xl font-bold text-blue-600 mb-6">
                          ₪ {product.price.toLocaleString()}
                          {product.previous_price && (
                            <span className="text-lg text-gray-500 line-through ml-2">
                              ₪ {product.previous_price.toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex flex-col sm:flex-row gap-4">
                        <Link 
                          to={`/products/${product.id}`}
                          className="bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-bold hover:bg-blue-700 transition flex-1 text-center"
                        >
                          {i18n.language === 'he' ? 'פרטי המוצר' : 'Product Details'}
                        </Link>
                                                 <Link 
                           to="/service#always-here-for-you"
                           className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-3 rounded-lg text-lg font-bold hover:bg-blue-50 transition flex-1 text-center"
                         >
                           {i18n.language === 'he' ? 'צור קשר' : 'Contact Us'}
                         </Link>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="relative">
                  {blueboardProducts.map((product) => (
                    <div key={product.id} className="relative">
                      <img 
                        src={product.image} 
                        alt={product.translations[i18n.language as 'en' | 'he'].name}
                        className="w-full h-auto rounded-2xl shadow-2xl"
                      />
                      <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                        {i18n.language === 'he' ? 'Made in Israel' : 'Made in Israel'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Product Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-6 rounded-lg text-center text-white shadow-lg">
              <h3 className="text-xl font-bold mb-2">BlueBoard</h3>
              <p className="mb-4 opacity-90">
                {i18n.language === 'he' ? 'הסקייטבורד המקורי שלנו' : 'Our Original Skateboard'}
              </p>
                             <Link to="/service#always-here-for-you" className="bg-white text-blue-600 px-4 py-2 rounded hover:bg-gray-100 transition font-bold">
                 {i18n.language === 'he' ? 'צור קשר' : 'Contact Us'}
               </Link>
            </div>
            
            <div className="bg-green-50 p-6 rounded-lg text-center">
              <h3 className="text-xl font-bold text-green-900 mb-2">
                {i18n.language === 'he' ? 'אביזרים ושירות' : 'Accessories & Service'}
              </h3>
              <p className="text-green-700 mb-4">
                {i18n.language === 'he' ? 'חלקים, שירות ותמיכה לרוכבים קיימים וחדשים' : 'Parts, service, and support for new and existing riders'}
              </p>
              <Link to="/service#always-here-for-you" className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition">
                {i18n.language === 'he' ? 'צור קשר' : 'Contact Us'}
              </Link>
            </div>
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {otherSkateboardProducts.map((product) => (
              <div key={product.id} className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <Link to={`/products/${product.id}`}>
                  <img 
                    src={product.image} 
                    alt={product.translations[i18n.language as 'en' | 'he'].name}
                    className="w-full h-64 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {product.translations[i18n.language as 'en' | 'he'].name}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {product.translations[i18n.language as 'en' | 'he'].description}
                    </p>
                    {typeof product.price === 'number' && (
                      <div className="text-2xl font-bold text-blue-600">
                        ₪ {product.price.toLocaleString()}
                      </div>
                    )}
                  </div>
                </Link>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="text-center mt-12 bg-gray-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {i18n.language === 'he' ? 'מוכן לנסות?' : 'Ready to Try?'}
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              {i18n.language === 'he' 
                ? 'צור קשר עכשיו לנסיעת מבחן חינם'
                : 'Contact us now for a free test ride'
              }
            </p>
                         <Link 
               to="/service#always-here-for-you" 
               className="bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-bold hover:bg-blue-700 transition"
             >
               {i18n.language === 'he' ? 'צור קשר' : 'Contact Us'}
             </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default ElectricSkateboardsPage; 
